/** Worker-owned clock that self-drives through main-thread stalls and softly re-locks to rAF anchors. */
export class HybridRenderClock {
    #anchorFreshnessMs;
    #maximumCorrectionMs;
    #onError;
    #scheduler;
    #targetFrameMs;
    #acceptedAnchors = 0;
    #anchorSequence = 0;
    #anchorTimestamp;
    #anchoredFrames = 0;
    #callback;
    #frameSequence = 0;
    #frames = 0;
    #ignoredAnchors = 0;
    #lastFrameTimestamp;
    #maximumFrameGapMs = 0;
    #lastRequestedDelayMs = 0;
    #requestedAt = 0;
    #maximumTimerLatenessMs = 0;
    #maximumCallbackMs = 0;
    #nextDeadline = 0;
    #overruns = 0;
    #running = false;
    #selfDrivenFrames = 0;
    #timer;
    constructor(options = {}) {
        this.#targetFrameMs = positiveFinite(options.targetFrameMs ?? 1000 / 60, "targetFrameMs");
        this.#anchorFreshnessMs = positiveFinite(options.anchorFreshnessMs ?? 100, "anchorFreshnessMs");
        this.#maximumCorrectionMs = nonNegativeFinite(options.maximumCorrectionMs ?? 2, "maximumCorrectionMs");
        if (this.#maximumCorrectionMs > this.#targetFrameMs / 2) {
            throw new RangeError("maximumCorrectionMs must not exceed half a frame");
        }
        this.#scheduler = options.scheduler ?? browserScheduler;
        this.#onError = options.onError;
    }
    start(callback) {
        if (this.#running)
            throw new Error("render clock is already running");
        this.#callback = callback;
        this.#running = true;
        this.#nextDeadline = this.#scheduler.now();
        this.schedule(0);
    }
    stop() {
        if (!this.#running)
            return;
        this.#running = false;
        this.#callback = undefined;
        if (this.#timer !== undefined)
            this.#scheduler.clearTimer(this.#timer);
        this.#timer = undefined;
    }
    /** Supplies a main-thread rAF anchor. Duplicate and stale messages are ignored. */
    anchor(sequence, timestamp) {
        const anchorSequence = positiveU32(sequence, "anchor sequence");
        const anchorTimestamp = finite(timestamp, "anchor timestamp");
        if (this.#anchorSequence !== 0 && !isNewerSequence(anchorSequence, this.#anchorSequence)) {
            this.#ignoredAnchors += 1;
            return false;
        }
        this.#anchorSequence = anchorSequence;
        this.#anchorTimestamp = anchorTimestamp;
        this.#acceptedAnchors += 1;
        return true;
    }
    metrics() {
        return {
            acceptedAnchors: this.#acceptedAnchors,
            anchoredFrames: this.#anchoredFrames,
            frames: this.#frames,
            ignoredAnchors: this.#ignoredAnchors,
            maximumFrameGapMs: this.#maximumFrameGapMs,
            lastRequestedDelayMs: this.#lastRequestedDelayMs,
            maximumTimerLatenessMs: this.#maximumTimerLatenessMs,
            maximumCallbackMs: this.#maximumCallbackMs,
            overruns: this.#overruns,
            running: this.#running,
            selfDrivenFrames: this.#selfDrivenFrames,
        };
    }
    tick = () => {
        this.#timer = undefined;
        if (!this.#running)
            return;
        const now = this.#scheduler.now();
        if (this.#requestedAt !== 0) {
            const lateness = now - this.#requestedAt - this.#lastRequestedDelayMs;
            this.#maximumTimerLatenessMs = Math.max(this.#maximumTimerLatenessMs, lateness);
        }
        const previousTimestamp = this.#lastFrameTimestamp;
        const deltaMs = previousTimestamp === undefined ? 0 : Math.max(0, now - previousTimestamp);
        if (previousTimestamp !== undefined) {
            this.#maximumFrameGapMs = Math.max(this.#maximumFrameGapMs, deltaMs);
        }
        const anchorFresh = this.#anchorTimestamp !== undefined &&
            now >= this.#anchorTimestamp &&
            now - this.#anchorTimestamp <= this.#anchorFreshnessMs;
        let correctionMs = 0;
        let nextDeadline = this.#nextDeadline + this.#targetFrameMs;
        if (anchorFresh && this.#anchorTimestamp !== undefined) {
            const aligned = nextAlignedFrame(now, this.#anchorTimestamp, this.#targetFrameMs);
            correctionMs = clamp(aligned - nextDeadline, -this.#maximumCorrectionMs, this.#maximumCorrectionMs);
            nextDeadline += correctionMs;
            this.#anchoredFrames += 1;
        }
        else {
            this.#selfDrivenFrames += 1;
        }
        if (nextDeadline < now - this.#targetFrameMs) {
            nextDeadline = now;
            correctionMs = 0;
            this.#overruns += 1;
        }
        this.#frameSequence = nextFrameSequence(this.#frameSequence);
        this.#frames += 1;
        this.#lastFrameTimestamp = now;
        this.#nextDeadline = nextDeadline;
        const callbackStart = this.#scheduler.now();
        try {
            this.#callback?.({
                anchorFresh,
                correctionMs,
                deltaMs,
                frameSeq: this.#frameSequence,
                timestamp: now,
            });
        }
        catch (cause) {
            // A thrown value is not always an Error: wasm-bindgen rejects with a
            // JsValue, and reporting only the wrapper's message loses the one thing
            // that says what actually failed.
            const error = cause instanceof Error
                ? cause
                : new Error(`render clock callback failed: ${describe(cause)}`, { cause });
            this.stop();
            this.#onError?.(error);
            return;
        }
        this.#maximumCallbackMs = Math.max(this.#maximumCallbackMs, this.#scheduler.now() - callbackStart);
        if (this.#running)
            this.schedule(Math.max(0, nextDeadline - this.#scheduler.now()));
    };
    schedule(delayMs) {
        this.#lastRequestedDelayMs = delayMs;
        this.#requestedAt = this.#scheduler.now();
        this.#timer = this.#scheduler.setTimer(this.tick, delayMs);
    }
}
export function nextAlignedFrame(now, anchor, targetFrameMs) {
    finite(now, "now");
    finite(anchor, "anchor");
    positiveFinite(targetFrameMs, "targetFrameMs");
    const elapsed = Math.max(0, now - anchor);
    return anchor + (Math.floor(elapsed / targetFrameMs) + 1) * targetFrameMs;
}
const browserScheduler = {
    clearTimer: (handle) => clearTimeout(handle),
    now: () => performance.timeOrigin + performance.now(),
    setTimer: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
};
function isNewerSequence(candidate, previous) {
    const distance = (candidate - previous) >>> 0;
    return distance !== 0 && distance < 0x8000_0000;
}
function nextFrameSequence(value) {
    const next = (value + 1) >>> 0;
    return next === 0 ? 1 : next;
}
function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
}
function positiveU32(value, label) {
    if (!Number.isInteger(value) || value < 1 || value > 0xffff_ffff) {
        throw new RangeError(`${label} must be a positive u32`);
    }
    return value;
}
function finite(value, label) {
    if (!Number.isFinite(value))
        throw new RangeError(`${label} must be finite`);
    return value;
}
function positiveFinite(value, label) {
    if (!Number.isFinite(value) || value <= 0)
        throw new RangeError(`${label} must be positive`);
    return value;
}
function nonNegativeFinite(value, label) {
    if (!Number.isFinite(value) || value < 0)
        throw new RangeError(`${label} must be non-negative`);
    return value;
}
/** Renders a thrown non-Error value well enough to diagnose it. */
function describe(cause) {
    if (typeof cause === "string")
        return cause;
    if (typeof cause === "object" && cause !== null && "message" in cause) {
        return String(cause.message);
    }
    return String(cause);
}
//# sourceMappingURL=render-clock.js.map